/**
 * Property Requests API
 * RTK Query endpoints for property request (lease/buy) management
 */

import { baseApi } from "./baseApi";

/**
 * Property Request interface matching backend
 */
export interface PropertyRequest {
	id: string;
	organization_id?: string;
	property_id: string;
	property_name?: string;
	request_type: "lease" | "buy";
	status: "pending" | "under_review" | "accepted" | "declined" | "expired";
	first_name: string;
	last_name: string;
	email: string;
	phone: string;
	employment_status: "employed" | "self_employed" | "student" | "unemployed";
	employer_name?: string;
	business_name?: string;
	profession?: string;
	quoted_amount: number;
	offer_amount?: number;
	willing_to_pay_quoted: boolean;
	requester_id?: string;
	reviewed_by_id?: string;
	reviewed_at?: string;
	conversation_id?: string; // Link to conversation for negotiation
	created_at: string;
	updated_at: string;
}

/**
 * Property request list response interface
 */
export interface PropertyRequestListResponse {
	count: number;
	next: string | null;
	previous: string | null;
	results: PropertyRequest[];
}

/**
 * Create property request request interface
 */
export interface PropertyRequestData {
	property_id: string;
	request_type: "lease" | "buy";
	first_name: string;
	last_name: string;
	email: string;
	phone: string;
	employment_status: "employed" | "self_employed" | "student" | "unemployed";
	employer_name?: string;
	business_name?: string;
	profession?: string;
	quoted_amount: number;
	offer_amount?: number;
	willing_to_pay_quoted: boolean;
}

/**
 * Update property request request interface
 */
export interface PropertyRequestUpdateData {
	status?: "pending" | "under_review" | "accepted" | "declined" | "expired";
	review_notes?: string;
}

/**
 * Property Requests API endpoints
 */
export const propertyRequestsApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		/**
		 * Create a new property request (public endpoint)
		 */
		createPropertyRequest: builder.mutation<PropertyRequest, PropertyRequestData>({
			query: (data) => ({
				url: "/property-requests/",
				method: "POST",
				body: data,
			}),
			invalidatesTags: ["PropertyRequests"],
		}),

		/**
		 * List all property requests (admin only)
		 */
		listPropertyRequests: builder.query<
			PropertyRequestListResponse,
			{
				status?: string;
				request_type?: string;
				property_id?: string;
				email?: string;
				search?: string;
				ordering?: string;
				page?: number;
				page_size?: number;
			}
		>({
			query: (params) => {
				const queryParams = new URLSearchParams();
				if (params.status) queryParams.append("status", params.status);
				if (params.request_type) queryParams.append("request_type", params.request_type);
				if (params.property_id) queryParams.append("property_id", params.property_id);
				if (params.email) queryParams.append("email", params.email);
				if (params.search) queryParams.append("search", params.search);
				if (params.ordering) queryParams.append("ordering", params.ordering);
				if (params.page) queryParams.append("page", params.page.toString());
				if (params.page_size) queryParams.append("page_size", params.page_size.toString());

				const queryString = queryParams.toString();
				return {
					url: queryString ? `/property-requests/?${queryString}` : `/property-requests/`,
					method: "GET",
				};
			},
			providesTags: ["PropertyRequests"],
		}),

		/**
		 * Get property request details by ID (admin only)
		 */
		getPropertyRequest: builder.query<PropertyRequest, string>({
			query: (id) => ({
				url: `/property-requests/${id}`,
				method: "GET",
			}),
			providesTags: (result, error, id) => [{ type: "PropertyRequests", id }],
		}),

		/**
		 * Get property request details by ID (public endpoint)
		 */
		getPublicPropertyRequest: builder.query<PropertyRequest, string>({
			query: (id) => ({
				url: `/property-requests/public/${id}`,
				method: "GET",
			}),
		}),

		/**
		 * Update a property request (admin only)
		 */
		updatePropertyRequest: builder.mutation<
			PropertyRequest,
			{ id: string; data: PropertyRequestUpdateData }
		>({
			query: ({ id, data }) => ({
				url: `/property-requests/${id}`,
				method: "PATCH",
				body: data,
			}),
			invalidatesTags: (result, error, { id }) => [
				{ type: "PropertyRequests", id },
				"PropertyRequests",
			],
		}),
	}),
});

export const {
	useCreatePropertyRequestMutation,
	useListPropertyRequestsQuery,
	useLazyListPropertyRequestsQuery,
	useGetPropertyRequestQuery,
	useLazyGetPropertyRequestQuery,
	useGetPublicPropertyRequestQuery,
	useLazyGetPublicPropertyRequestQuery,
	useUpdatePropertyRequestMutation,
} = propertyRequestsApi;
